import React, { useRef, useEffect, useState } from 'react';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS } from '../designSystem';
import { getCurrentUser, hasPermission, PERMISSIONS } from '../authUtils';
import { PageHeaderProvider, usePageHeader } from '../contexts/PageHeaderContext';
import GlobalSearch from '../components/GlobalSearch';

const SIDEBAR_WIDTH = 280;

function AppLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { headerConfig } = usePageHeader();
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  const navLinkStyle = ({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.MD,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    color: isActive ? COLORS.PRIMARY : 'rgba(255, 255, 255, 0.65)',
    textDecoration: 'none',
    fontWeight: isActive ? '700' : '500',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    backgroundColor: isActive ? 'rgba(77, 219, 148, 0.18)' : 'transparent',
    borderLeft: isActive ? `4px solid ${COLORS.PRIMARY}` : '4px solid transparent',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
  });

  return (
    <div style={styles.wrapper}>
      {/* LEFT SIDEBAR */}
      <aside style={styles.sidebar}>
        {/* Sidebar Header with Logo */}
        <div style={styles.sidebarHeader}>
          <a href="/dashboard" style={styles.sidebarLogoLink}>
            <img
              src="/assets/logo.png"
              alt="Emerald MCA"
              style={{ height: "32px", width: 'auto' }}
            />
            <div>
              <div style={styles.logoText}>EMERALD</div>
              <div style={styles.logoSubtext}>MCA</div>
            </div>
          </a>
        </div>

        {/* Primary Navigation Group */}
        <nav style={styles.sidebarNav}>
          <div style={styles.navGroup}>
            <div style={styles.navGroupLabel}>NAVIGATION</div>
            <NavLink
              to="/dashboard"
              style={({ isActive }) => ({ ...navLinkStyle({ isActive }) })}
              onMouseEnter={(e) => {
                if (location.pathname !== '/dashboard') {
                  e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/dashboard') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>🏠</span>
              <span>Dashboard</span>
            </NavLink>
            <NavLink
              to="/merchants"
              style={({ isActive }) => ({ ...navLinkStyle({ isActive }) })}
              onMouseEnter={(e) => {
                if (location.pathname !== '/merchants') {
                  e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/merchants') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>👥</span>
              <span>Merchants</span>
            </NavLink>
            <NavLink
              to="/deals"
              style={({ isActive }) => ({ ...navLinkStyle({ isActive }) })}
              onMouseEnter={(e) => {
                if (location.pathname !== '/deals') {
                  e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/deals') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>💰</span>
              <span>Advances</span>
            </NavLink>
            <NavLink
              to="/funders"
              style={({ isActive }) => ({ ...navLinkStyle({ isActive }) })}
              onMouseEnter={(e) => {
                if (location.pathname !== '/funders') {
                  e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/funders') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>🏦</span>
              <span>Funders</span>
            </NavLink>
            {hasPermission(PERMISSIONS.USER_MANAGE) && (
              <NavLink
                to="/team"
                style={({ isActive }) => ({ ...navLinkStyle({ isActive }) })}
                onMouseEnter={(e) => {
                  if (location.pathname !== '/team') {
                    e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.06)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== '/team') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '18px' }}>👤</span>
                <span>Team</span>
              </NavLink>
            )}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div style={styles.sidebarFooter}>
          <div style={styles.navGroup}>
            <div style={styles.navGroupLabel}>SETTINGS</div>
            <button
              onClick={() => navigate('/settings/funder')}
              style={{ ...navLinkStyle({ isActive: false }), width: '100%', backgroundColor: 'transparent', border: 'none' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ fontSize: '18px' }}>⚙️</span>
              <span>Settings</span>
            </button>
            <button
              onClick={handleLogout}
              style={{ ...navLinkStyle({ isActive: false }), width: '100%', backgroundColor: 'transparent', border: 'none', color: '#ff6b6b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 107, 107, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ fontSize: '18px' }}>🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* TOP NAVIGATION BAR - UNIFIED HEADER */}
      <div style={styles.topNavContainer}>
        <nav style={styles.topNav}>
          {/* LEFT: Back Button */}
          {headerConfig.backButton && (
            <div style={{ marginRight: SPACING.LG, flexShrink: 0 }}>
              {headerConfig.backButton}
            </div>
          )}

          {/* LEFT-ALIGNED: Page Title and Subtitle (compact) */}
          <div style={{ flex: '0 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', marginRight: SPACING.LG }}>
            {headerConfig.title && (
              <div style={{
                ...TYPOGRAPHY.H3,
                color: COLORS.TEXT_PRIMARY,
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {headerConfig.title}
              </div>
            )}
            {headerConfig.subtitle && (
              <div style={{
                ...TYPOGRAPHY.BODY_SM,
                color: COLORS.TEXT_SECONDARY,
                margin: `${SPACING.XS} 0 0 0`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {headerConfig.subtitle}
              </div>
            )}
          </div>

          {/* CENTER: Global Search (prominent, flexible width) */}
          <div style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center', minWidth: '280px', marginRight: SPACING.LG }}>
            <GlobalSearch />
          </div>

          {/* RIGHT: Action Buttons */}
          {headerConfig.actions && headerConfig.actions.length > 0 && (
            <div style={{ display: 'flex', gap: SPACING.MD, marginRight: SPACING.XL, flexShrink: 0, alignItems: 'center' }}>
              {headerConfig.actions}
            </div>
          )}

          {/* FAR RIGHT: User Section */}
          <div style={styles.userSection} ref={dropdownRef}>
            <button
              style={styles.userButton}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span style={{ fontSize: '16px' }}>👤</span>
              <span style={{ fontWeight: '500' }}>
                {user?.email?.split('@')[0] || 'User'}
              </span>
              <span style={{ fontSize: '12px', opacity: 0.6 }}>▼</span>
            </button>

            {dropdownOpen && (
              <div style={styles.dropdown}>
                <a href="/settings/funder" style={styles.dropdownItem}>
                  <span>⚙️</span> Account Settings
                </a>
                <div style={styles.dropdownDivider} />
                <button
                  onClick={handleLogout}
                  style={{ ...styles.dropdownItem, color: '#dc3545', border: 'none', width: '100%', textAlign: 'left' }}
                >
                  <span>🚪</span> Log out
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>

      {/* MAIN CONTENT AREA */}
      <main style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

// Wrapper component to provide context
export default function AppLayout() {
  return (
    <PageHeaderProvider>
      <AppLayoutInner />
    </PageHeaderProvider>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f9f9f9',
  },

  // SIDEBAR STYLES
  sidebar: {
    position: 'fixed',
    left: 0,
    top: 0,
    width: `${SIDEBAR_WIDTH}px`,
    height: '100vh',
    backgroundColor: '#0d1b17',
    background: `linear-gradient(180deg, #0d1b17 0%, #0a1410 100%)`,
    borderRight: `2px solid ${COLORS.BORDER_ACCENT}`,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 999,
    overflow: 'hidden',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.4)',
  },

  sidebarHeader: {
    padding: `${SPACING.XL} ${SPACING.LG}`,
    borderBottom: '1px solid rgba(77, 219, 148, 0.2)',
    background: 'linear-gradient(135deg, rgba(77, 219, 148, 0.12) 0%, rgba(77, 219, 148, 0.08) 100%)',
    flexShrink: 0,
  },

  sidebarLogoLink: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.MD,
    textDecoration: 'none',
    color: '#ffffff',
    transition: 'all 0.3s ease',
    padding: SPACING.MD,
    marginLeft: `-${SPACING.MD}`,
    marginTop: `-${SPACING.MD}`,
    marginBottom: `-${SPACING.MD}`,
    borderRadius: RADIUS.DEFAULT,
  },

  logoIcon: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  logoText: {
    fontSize: '13px',
    fontWeight: '800',
    letterSpacing: '0.5px',
    color: '#ffffff',
    lineHeight: 1.1,
  },

  logoSubtext: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.5)',
    letterSpacing: '0.3px',
  },

  sidebarNav: {
    flex: 1,
    overflow: 'auto',
    padding: `${SPACING.MD} 0`,
    display: 'flex',
    flexDirection: 'column',
  },

  navGroup: {
    marginBottom: SPACING.MD,
  },

  navGroupLabel: {
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    color: 'rgba(255, 255, 255, 0.4)',
    padding: `${SPACING.SM} ${SPACING.LG} ${SPACING.XS}`,
    textTransform: 'uppercase',
  },

  sidebarFooter: {
    borderTop: '1px solid rgba(77, 219, 148, 0.15)',
    padding: `${SPACING.MD} 0`,
    flexShrink: 0,
    background: 'linear-gradient(to top, rgba(77, 219, 148, 0.06), transparent)',
  },

  // TOP NAV STYLES - UNIFIED HEADER
  topNavContainer: {
    position: 'fixed',
    top: 0,
    left: SIDEBAR_WIDTH,
    right: 0,
    height: '80px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    zIndex: 998,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },

  topNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    gap: SPACING.MD,
  },

  userSection: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },

  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SM,
    padding: `${SPACING.SM} ${SPACING.MD}`,
    backgroundColor: '#f6fdf9',
    color: COLORS.TEXT_PRIMARY,
    border: '1px solid #e5e7eb',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '500',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  dropdown: {
    position: 'absolute',
    top: '56px',
    right: 0,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: RADIUS.DEFAULT,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    minWidth: '200px',
    zIndex: 1001,
    overflow: 'hidden',
  },

  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.SM,
    width: '100%',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    color: COLORS.TEXT_PRIMARY,
    textDecoration: 'none',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    border: 'none',
    textAlign: 'left',
  },

  dropdownDivider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
    margin: 0,
  },

  // MAIN CONTENT STYLES
  main: {
    marginLeft: `${SIDEBAR_WIDTH}px`,
    marginTop: '80px',
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#f9f9f9',
  },
};
